<template>
	<div class="row-active-filters">
		<v-dropdown variant="bubble" color="blue" icon="sort-amount-asc" :label="sort_label">
			<fieldset>
				<label v-for="option in store.lists.sort" :key="option.value" class="radio">
					<input v-model="sort" type="radio" :value="option.value" @change="emit(`change`)" />

					<span>{{ option.label }}</span>
				</label>
			</fieldset>
		</v-dropdown>

		<v-dropdown variant="bubble" color="blue" icon="clock" :label="time_label" full_label="Posted date">
			<fieldset>
				<label class="radio">
					<input v-model="date" type="radio" value="" @change="emit(`change`)" />

					<span>Any time</span>
				</label>

				<label v-for="option in store.lists.posted_dates" :key="option.value" class="radio">
					<input v-model="date" type="radio" :value="option.value" @change="emit(`change`)" />

					<span>{{ option.label }}</span>
				</label>
			</fieldset>
		</v-dropdown>

		<filter-range
			v-if="location"
			v-model="radius"
			icon="location-dot"
			:label="location_label"
			full_label="Radius"
			heading="Distance from city"
			description="Within {value} miles"
			:min="5"
			:max="100"
			:step="5"
			@change="emit(`change`)"
		/>

		<filter-value-bubbles
			v-model="work_environments"
			:options="store.lists.work_environments"
			@change="emit(`change`)"
		/>

		<v-dropdown
			v-if="salary_visible"
			v-model:open="salary_dropdown_open"
			variant="bubble"
			color="orange"
			:label="salary_label"
			full_label="Salary"
		>
			<div class="pill-select">
				<button type="button" :class="{ btn: true, active: is_annual }" @click="set_period(`yr`)">
					Annual
				</button>

				<button type="button" :class="{ btn: true, active: !is_annual }" @click="set_period(`hr`)">
					Hourly
				</button>
			</div>

			<fieldset>
				<label v-for="option in salary_options" :key="option.value" class="radio">
					<input v-model.number="salary_radio_value" type="radio" :value="option.value" />

					<span>{{ option.label }}</span>
				</label>
			</fieldset>

			<button type="button" class="btn btn-solid salary-apply small" @click="apply_salary">Apply</button>
		</v-dropdown>

		<filter-value-bubbles v-model="work_schedules" :options="store.lists.work_schedules" @change="emit(`change`)" />

		<filter-value-bubbles
			v-model="employment_types"
			:options="store.lists.employment_types"
			@change="emit(`change`)"
		/>

		<filter-value-bubbles v-model="experiences" :options="store.lists.experiences" @change="emit(`change`)" />

		<filter-value-bubbles v-model="qualifications" :options="store.lists.qualifications" @change="emit(`change`)" />

		<a v-if="resettable" href="#" class="btn btn-link" @click.prevent="emit(`reset`)"> Clear all </a>
	</div>
</template>

<style lang="scss" scoped>
@use 'sass:math';

@use '/sass/variables' as *;
@use '/sass/toolkit' as *;

.row-active-filters {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 0.5em;
	margin-bottom: $spacer;
}

@include media('<=phone') {
	.row-active-filters {
		margin-bottom: math.div($spacer, 2);
	}
}

fieldset {
	@include filter-option-list;
}

.pill-select {
	@include pill-select;
	margin: 0 1em 0.5em;
}

.salary-apply {
	width: calc(100% - 2em);
	margin: 0.5em 1em 0;
}

.btn-link {
	font-size: 0.875rem;
	font-weight: bold;
}
</style>

<script setup>
import { computed, ref, watch } from 'vue';

import FilterRange from './_filter-range.vue';
import FilterValueBubbles from './_filter-value-bubbles.vue';
import { store } from './_store.js';
import { use_salary_filter } from './_use-salary-filter.js';
import VDropdown from './_v-dropdown.vue';

const props = defineProps({
	resettable: {
		type: Boolean,
		required: false,
	},
	location: {
		type: String,
		required: false,
		default: ``,
	},
});

const emit = defineEmits([`change`, `reset`]);

const sort = defineModel(`sort`, {
	type: String,
	default: ``,
});

const date = defineModel(`date`, {
	type: String,
	default: ``,
});

const work_environments = defineModel(`work_environments`, {
	type: Array,
	default: () => [],
});

const work_schedules = defineModel(`work_schedules`, {
	type: Array,
	default: () => [],
});

const employment_types = defineModel(`employment_types`, {
	type: Array,
	default: () => [],
});

const experiences = defineModel(`experiences`, {
	type: Array,
	default: () => [],
});

const qualifications = defineModel(`qualifications`, {
	type: Array,
	default: () => [],
});

const salary_period = defineModel(`salary_period`, {
	type: String,
	default: ``,
});

const require_salary = defineModel(`require_salary`, {
	type: String,
	default: ``,
});

const salary_min = defineModel(`salary_min`, {
	type: Number,
	default: null,
});

const salary_draft_period = ref(salary_period.value);

const salary_draft_require = ref(require_salary.value);

const salary_draft_min = ref(salary_min.value);

const {
	is_annual,
	options: salary_options,
	radio_value: salary_radio_value,
	set_period,
	// eslint-disable-next-line no-empty-function
} = use_salary_filter(salary_draft_period, salary_draft_require, salary_draft_min, () => {});

const { options: salary_applied_options, radio_value: salary_applied_radio_value } = use_salary_filter(
	salary_period,
	require_salary,
	salary_min,
	// eslint-disable-next-line no-empty-function
	() => {},
);

const sort_label = computed(() => store.lists.sort.find((option) => option.value === sort.value)?.label ?? `Relevance`);

const time_label = computed(() => {
	if (date.value === ``) {
		return `Any time`;
	}

	return store.lists.posted_dates.find((option) => option.value === date.value)?.label ?? `Any time`;
});

const radius = defineModel(`radius`, {
	type: Number,
	default: 25,
});

const location_label = computed(() => `${props.location} · ${radius.value} mi`);

const salary_dropdown_open = ref(false);

watch(salary_dropdown_open, () => {
	salary_draft_period.value = salary_period.value;
	salary_draft_require.value = require_salary.value;
	salary_draft_min.value = salary_min.value;
});

const salary_active = computed(() => salary_applied_radio_value.value !== null);

const salary_visible = computed(() => salary_active.value || salary_dropdown_open.value);

const salary_label = computed(() => {
	for (const option of salary_applied_options.value) {
		if (option.value !== salary_applied_radio_value.value) {
			continue;
		}

		return option.value === null ? `Salary` : option.label;
	}

	return `Salary`;
});

function apply_salary() {
	salary_period.value = salary_draft_period.value;
	require_salary.value = salary_draft_require.value;
	salary_min.value = salary_draft_min.value;

	emit(`change`);

	salary_dropdown_open.value = false;
}
</script>
